import { useTranslation } from "react-i18next";
import { Globe } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

/**
 * Compact language toggle for the navbar.
 *
 * Displays the OPPOSITE language as the action (RO when on EN, EN when on RO)
 * so users always see what they'd switch INTO, not what they're already in.
 * Persists choice to localStorage via i18next LanguageDetector config.
 *
 * Iter 72: when the user is logged in, also push the choice to the backend so
 * transactional emails (verification re-send, password reset) honour the same
 * language. Best-effort — failures are silently ignored.
 */
export default function LanguageToggle({ className = "" }) {
  const { i18n } = useTranslation();
  const { user } = useAuth() || {};
  const current = i18n.resolvedLanguage || i18n.language || "ro";
  const next = current.startsWith("en") ? "ro" : "en";

  const handleClick = () => {
    i18n.changeLanguage(next);
    if (user && user.id) {
      api.post("/auth/language", { language: next }).catch(() => {
        // Best-effort sync; localStorage is the source of truth for the UI.
      });
    }
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium text-zinc-300 hover:text-white hover:bg-white/5 transition ${className}`}
      data-testid="language-toggle"
      aria-label={`Switch to ${next.toUpperCase()}`}
      title={`Switch to ${next.toUpperCase()}`}
    >
      <Globe size={14} strokeWidth={2} />
      <span className="uppercase tracking-wider">{next}</span>
    </button>
  );
}
